import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;

    // xng
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },

    // xng
    container: {
        css: css`
            overflow: auto;
            margin-bottom: 12px;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-bottom: 12px;
        `,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        const quoteSettings = settingsCollection.quoteSettings;

        return (
            <StyledWrapper>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        as="h3"
                        variant="h3"
                        css={css`
                            margin-bottom: 0;
                            text-transform: capitalize;
                        `}
                    >
                        {translate("Recent Quotes")}
                    </Typography>
                    <Link
                        href={myQuotesPageUrl}
                        color="secondary"
                        mergeCss={true}
                        className="view-all-link"
                        css={css`
                            > span {
                                display: flex;
                                align-items: center;
                                margin-left: 32px;
                                font-size: 15px;
                                font-weight: bold;
                            }
                        `}
                    >
                        {translate("View All")}
                        <Icon
                            src="ChevronRight"
                            size={14}
                            color="secondary"
                            css={css`
                                margin-left: 5px;
                            `}
                        ></Icon>
                    </Link>
                </StyledWrapper>
                {quotesDataView.value.length === 0 && (
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography
                            as="p"
                            {...styles.noResultsText}
                            data-test-selector="orderHistoryTable_noQuotesFound"
                        >
                            {siteMessage("Rfq_NoQuotesMessage")}
                        </Typography>
                    </StyledWrapper>
                )}
                {quotesDataView.value.length > 0 && (
                    <StyledWrapper {...styles.container} data-test-selector="widget_RecentOrders">
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                <DataTableHeader>{translate("Quote #", "quoteNumber")}</DataTableHeader>
                                {quoteSettings.jobQuoteEnabled && (
                                    <DataTableHeader>{translate("Type")}</DataTableHeader>
                                )}
                                <DataTableHeader>{translate("Status", "status")}</DataTableHeader>
                                <DataTableHeader>{translate("Requested", "Requested")}</DataTableHeader>
                                <DataTableHeader>{translate("Expires", "Expires")}</DataTableHeader>
                                {session.isSalesPerson && (
                                    <>
                                        <DataTableHeader>{translate("Sales Rep", "salesRep")}</DataTableHeader>
                                        <DataTableHeader>{translate("User", "User")}</DataTableHeader>
                                    </>
                                )}
                                <DataTableHeader>{translate("Customer", "Customer")}</DataTableHeader>
                                {!session.isSalesPerson && (
                                    <DataTableHeader>{translate("Ship To / Pick Up", "shipTo/PickUp")}</DataTableHeader>
                                )}
                            </DataTableHead>

                            <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                                {quotesDataView.value.map(quote => {
                                    if (quote === null) {
                                        return null;
                                    }
                                    const orderDateDisplay = getLocalizedDateTime({
                                        dateTime: quote.orderDate!,
                                        language: session.language,
                                        options: { year: "2-digit", month: "numeric", day: "numeric" },
                                    });

                                    let expirationDateDisplay = "";
                                    if (quote.expirationDate) {
                                        expirationDateDisplay = getLocalizedDateTime({
                                            dateTime: quote.expirationDate,
                                            language: session.language,
                                            options: { year: "2-digit", month: "numeric", day: "numeric" },
                                        });
                                    }
                                    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

                                    return (
                                        <DataTableRow key={quote.id}>
                                            <DataTableCell>
                                                {quote.quoteNumber && (
                                                    <RfqQuoteDetailsPageTypeLink
                                                        title={quote.quoteNumber}
                                                        quoteId={quote.id}
                                                    />
                                                )}
                                            </DataTableCell>
                                            {quoteSettings.jobQuoteEnabled && (
                                                <DataTableCell>{quote.typeDisplay}</DataTableCell>
                                            )}
                                            <DataTableCell>{quote.statusDisplay}</DataTableCell>
                                            <DataTableCell>{orderDateDisplay}</DataTableCell>
                                            <DataTableCell>{expirationDateDisplay || null}</DataTableCell>
                                            {session.isSalesPerson && (
                                                <>
                                                    <DataTableCell>{quote.salespersonName}</DataTableCell>
                                                    <DataTableCell>{quote.userName}</DataTableCell>
                                                </>
                                            )}
                                            <DataTableCell>{customerDisplay}</DataTableCell>
                                            {!session.isSalesPerson && (
                                                <DataTableCell>{quote.shipToFullAddress}</DataTableCell>
                                            )}
                                        </DataTableRow>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
